import React, { useEffect, useState } from "react";
import { showSuccess, showError } from "../../../utils/toastUtils";
import { useLoader } from "../../../utils/LoaderUtils";
import smsAPI from "../../../api/notification/smsApi";
import useNumericInput from "../../../hooks/useNumericInput";

// Standard Pre-approved Templates Config
const SYSTEM_SMS_TEMPLATES = [
  {
    eventKey: "customer.registration",
    title: "Customer Welcome Registration",
    fixedText: "Welcome to {var2}! Your account has been successfully created. Enjoy seamless shopping with us.",
    variables: "{var1} = Customer Name, {var2} = Store Name",
  },
  {
    eventKey: "customer.login.otp",
    title: "Login OTP / Verification",
    fixedText: "OTP for Mobile verification on {var1} is {var2} {var3}",
    variables: "{var1} = OTP Code, {var2} = Store Name",
  },
  {
    eventKey: "order.confirmation",
    title: "Order Confirmation (Customer)",
    fixedText: "Hi {var1}, thank you for shopping! Your order #{var3} of Rs. {var4} is confirmed and being processed. {var2}",
    variables: "{var1} = Customer Name, {var2} = Store Name, {var3} = Order ID, {var4} = Total Amount",
  },
  {
    eventKey: "admin.order.alert",
    title: "New Order Alert (Admin)",
    fixedText: "Alert: New order #{var1} received on {var2} worth Rs. {var3}. Check admin panel for details.",
    variables: "{var1} = Order ID, {var2} = Store Name, {var3} = Total Amount",
  },
  {
    eventKey: "customer.forgot.password",
    title: "Forgot Password Link",
    fixedText: "Hi {var1}, use this link to reset your {var3} account password: {var2}. Link valid for 15 mins.",
    variables: "{var1} = Customer Name, {var2} = Reset Link, {var3} = Store Name",
  },
  {
    eventKey: "admin.customize.order",
    title: "Custom Form Request (Admin)",
    fixedText: "New custom request #{var1} received on {var2}. Please review submission details in dashboard.",
    variables: "{var1} = Submission ID, {var2} = Store Name",
  },
];

function SmsTemplateSettings() {
  useNumericInput();
  const { showLoader, hideLoader } = useLoader();
  const [eventTemplates, setEventTemplates] = useState({});
  const [isSavingTemplates, setIsSavingTemplates] = useState(false);

  const fetchTemplateDetails = async () => {
    try {
      showLoader();
      const res = await smsAPI.getSmsTemplates();

      if (res.code === 200) {
        // Map existing Event Templates from DB
        const savedTemplates = res.data || [];
        const templateMap = {};
        
        savedTemplates.forEach((t) => {
          templateMap[t.eventKey] = {
            dltTemplateId: t.dltTemplateId || "",
            enabled: t.enabled ?? true,
          };
        });
        
        setEventTemplates(templateMap);
      }
    } catch (err) {
      console.error(err);
      showError(err.response?.data?.message || "Failed to fetch template settings");
    } finally {
      hideLoader();
    }
  };

  useEffect(() => {
    fetchTemplateDetails();
  }, []);

  // Event Template change handler
  const handleTemplateIdChange = (eventKey, value) => {
    setEventTemplates((prev) => ({
      ...prev,
      [eventKey]: {
        ...(prev[eventKey] || {}),
        dltTemplateId: value,
      },
    }));
  };

  // Event Templates Bulk Save Handler
  const handleSaveEventTemplates = async () => {
    try {
      setIsSavingTemplates(true);
      showLoader();

      console.log(eventTemplates);

      const payload = Object.entries(eventTemplates).map(([eventKey, data]) => ({
        eventKey,
        dltTemplateId: data.dltTemplateId,
        enabled: true,
      }));

      const res = await smsAPI.addSmsTemplates({ eventTemplates: payload });

      if (res.code === 200 || res.status === "success") {
        showSuccess("DLT Template IDs saved successfully");
        fetchTemplateDetails();
      } else {
        showError(res.message || "Failed to update templates");
      }
    } catch (err) {
      showError(err.response?.data?.message || "Save operation failed");
    } finally {
      setIsSavingTemplates(false);
      hideLoader();
    }
  };

  return (
    <>
      <div className="card-header d-flex align-items-center justify-content-between">
        <h4>SMS Template Settings</h4>
      </div>

      <div className="card-body">
        <div className="row">
          <div className="col-xl-12">
            <div className="card border">
              <div className="card-header bg-light">
                <h5 className="fs-15 fw-semibold m-0">Standard DLT Template Mapping</h5>
                <p className="text-muted fs-12 mb-0">
                  Register these exact message formats on your DLT portal and enter the approved Template IDs below.
                </p>
              </div>

              <div className="card-body">
                {SYSTEM_SMS_TEMPLATES.map((tmpl) => (
                  <div className="border rounded p-3 mb-3 bg-white" key={tmpl.eventKey}>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6 className="fw-bold mb-0 text-dark">{tmpl.title}</h6>
                      <span className="badge bg-secondary fs-11">{tmpl.eventKey}</span>
                    </div>

                    <div className="mb-2">
                      <label className="fs-12 text-muted fw-medium mb-1">Approved Fixed Message Format:</label>
                      <div className="p-2 bg-light rounded text-dark fs-13 font-monospace border">
                        {tmpl.fixedText}
                      </div>
                      <span className="fs-11 text-info mt-1 d-block">
                        <strong>Variables:</strong> {tmpl.variables}
                      </span>
                    </div>

                    <div className="row mt-2">
                      <div className="col-md-6">
                        <label className="form-label fs-12 fw-medium">
                          DLT Template ID <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control form-control-sm only-numeric"
                          placeholder="e.g. 120716123456789"
                          value={eventTemplates[tmpl.eventKey]?.dltTemplateId || ""}
                          onChange={(e) => handleTemplateIdChange(tmpl.eventKey, e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                ))}

                <div className="d-flex justify-content-end mt-3">
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={isSavingTemplates}
                    onClick={handleSaveEventTemplates}
                  >
                    {isSavingTemplates ? "Saving..." : "Save DLT Templates"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default SmsTemplateSettings;